/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { z } from 'zod';
import i18n from '@shared/i18n';
import { isValidCNPJFormat, isValidCPFFormat } from './valide-formats';

/**
 * Translation shorthand scoped to the `common` namespace, used to localize the
 * validation messages on the schemas below.
 *
 * @param key i18n key within the `common` namespace.
 * @returns The translated message.
 */
const t = (key: string) => i18n.t(key, { ns: 'common' });

export const BR_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO',
  'MA','MT','MS','MG','PA','PB','PR','PE','PI',
  'RJ','RN','RS','RO','RR','SC','SP','SE','TO',
] as const;

export type BrazilianState = typeof BR_STATES[number];

export const nameSchema = z
  .string()
  .trim()
  .min(3, { message: t('validation.name.minLength') })
  .max(200, { message: t('validation.name.maxLength') });

export const cpfSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length === 11, { message: t('validation.cpf.length') })
  .refine((v) => !/^(\d)\1{10}$/.test(v), { message: t('validation.cpf.invalid') })
  .refine((v) => isValidCPFFormat(v), { message: t('validation.cpf.invalid') });

/**
 * A CNPJ, letters included.
 *
 * Alphanumeric since Receita Federal's July 2026 rollout: the raiz and the
 * ordem accept A-Z and only the two check digits stayed numeric. Stripping
 * non-digits here left a company with one holding eleven characters, which
 * failed the length check — the validator behind this already knew the
 * format, and never got the chance to read it.
 */
export const cnpjSchema = z
  .string()
  .transform((v) => v.replace(/[^0-9A-Za-z]/g, '').toUpperCase())
  .refine((v) => v.length === 14, { message: t('validation.cnpj.length') })
  .refine((v) => isValidCNPJFormat(v), { message: t('validation.cnpj.invalid') });

export const phoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length >= 10 && v.length <= 11, {
    message: t('validation.phone.length'),
  });

export const cepSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length === 8, { message: t('validation.cep.invalid') });

export const streetSchema = z
  .string()
  .trim()
  .min(2, { message: t('validation.street.minLength') })
  .max(200, { message: t('validation.street.maxLength') });

export const numberSchema = z
  .string()
  .trim()
  .min(1, { message: t('validation.number.required') })
  .max(10, { message: t('validation.number.maxLength') })
  .refine((v) => /^\d+$/.test(v), { message: t('validation.number.digitsOnly') });

export const neighborhoodSchema = z
  .string()
  .trim()
  .min(2, { message: t('validation.neighborhood.minLength') })
  .max(100, { message: t('validation.neighborhood.maxLength') });

export const citySchema = z
  .string()
  .trim()
  .min(2, { message: t('validation.city.minLength') })
  .max(100, { message: t('validation.city.maxLength') });

export const stateSchema = z.enum(BR_STATES, {
  errorMap: () => ({ message: t('validation.state.invalid') }),
});

export const creciSchema = z
  .string()
  .max(20, { message: t('validation.creci.maxLength') })
  .optional()
  .or(z.literal(''));

export const complementSchema = z
  .string()
  .max(100, { message: t('validation.complement.maxLength') })
  .optional()
  .or(z.literal(''));

export const descriptionSchema = z
  .string()
  .max(500, { message: t('validation.description.maxLength') })
  .optional()
  .or(z.literal(''));
